import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { generateClient } from "aws-amplify/api";
import { changeSlide } from "../../../graphql/mutations";
import { onSlideChange, onSlideControlToggle } from "../../../graphql/subscriptions";
import { fetchPollResults } from "../../../services/event/pollResults";
import UpdateCurrentSlide from "../../../services/event/update-current-slide";
import Cookies from "js-cookie";
const client = generateClient();
interface Slide {
  id: string;
  type: "image" | "pollQuestions" | "pollresult" | "video" | "video/mp4" | "cpeForm" | "HLSStream" | "customSlide" | "interactive_form";
  sequence: number;
  url?: string;
  pollId?: string;
  pollResults?: any[];
  collateralTypeId?:string;
}
interface SlideContextProps {
  slides: Slide[];
  currentSlideSequence: number;
  handleNextSlide: () => void;
  handlePreviousSlide: () => void;
  handleSlideChange: (sequence: number) => void;
  setSlides: React.Dispatch<React.SetStateAction<Slide[]>>;
  setCurrentSlideSequence: React.Dispatch<React.SetStateAction<number>>;
  slideControlEnabled: boolean;
  setSlideControlEnabled: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  isSyncing:boolean;
}
const SlideContext = createContext<SlideContextProps | undefined>(undefined);
export const SlideProvider: React.FC<{ children: React.ReactNode; eventId: string; role: number, slidesData: Slide[], selectedSlideSequence: number; userEmail: string, setSelectedSlideId?:any, slideControlEnabled: boolean}> = ({
  children,
  eventId,
  role,
  slidesData,
  selectedSlideSequence,
  userEmail,
  setSelectedSlideId,
  slideControlEnabled
}) => {
    
  const [slides, setSlides] = useState<Slide[]>(slidesData); // Use initial slides data
  const [currentSlideSequence, setCurrentSlideSequence] = useState<number>(selectedSlideSequence);
  const [, setSlideControlEnabled] = useState<boolean>();
  const [isSyncing, setIsSyncing] = useState(false);
  useEffect(() => {
    setSlides(slidesData);
    if (selectedSlideSequence != 0) {
      setCurrentSlideSequence(selectedSlideSequence);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlideSequence, slides, slidesData]);

  // useEffect(() => {
  //   // Subscribe to slide control toggle
  //   const subscription = client.graphql({ query: onSlideControlToggle });
  //   const observer = subscription.subscribe({
  //     next: ({ data }) => {
  //       const controlData = data.onSlideControlToggle;
  //       if (controlData.eventid === eventId) {
  //         setSlideControlEnabled(controlData.enableSlideControl);
  //         // Update the cookie with the new status (either true or false)
  //         updateSlideControlCookie(controlData.enableSlideControl);
  //       }
  //     },
  //     error: (error) => console.warn("Subscription error:", error),
  //   });
  //   return () => observer.unsubscribe();
  // }, [eventId]);

  const updateSlideControlCookie = (status: boolean) => {
    const cookieName = `${eventId}-${userEmail}`;
    const expiryTime = new Date();
    expiryTime.setHours(expiryTime.getHours() + 2); // Set expiration time to 2 hours
    Cookies.set(cookieName, status.toString(), { expires: expiryTime });
  };

  useEffect(() => {
    const cookieName = `${eventId}-${userEmail}`;
    // const cookieName = `${eventId}`;
    const savedSlideControlStatus = Cookies.get(cookieName);
    if (savedSlideControlStatus !== undefined) {
      // If cookie exists, set the state to the saved value (true or false)
      setSlideControlEnabled(savedSlideControlStatus === "true");
    }
  }, [eventId]);

  const handleSlideChange = useCallback(
    async (sequence: number) => {
      try{
        if (currentSlideSequence === sequence || isSyncing) return; // Prevent duplicate or concurrent changes
        setIsSyncing(true);
        const selectedSlide = slides.find((slide) => slide.sequence === sequence);

        if (!selectedSlide) {
            console.warn("Slide not found for sequence:", sequence);
            return;
        }
        if (role === 0 && !slideControlEnabled) {
            console.warn("Speaker is not allowed to change slides at this moment.");
            return;
        }

            const slideDetails = {
                id: eventId,
                url: selectedSlide.url || "",
                sequence: selectedSlide.sequence,
                type: selectedSlide.type,
                pollId: selectedSlide.pollId || "",
                pollResults: selectedSlide.pollResults || [],
                collateralTypeId:selectedSlide.collateralTypeId || selectedSlide.id,
            };
            // Fetch poll results if needed
            if (selectedSlide.type === "pollresult" && selectedSlide.pollId) {
                const pollResultsResponse = await fetchPollResults(selectedSlide.pollId);
                slideDetails.pollResults = pollResultsResponse.data.results || [];
            }
            
            await client.graphql({
                query: changeSlide,
                variables: slideDetails,
            });
            setCurrentSlideSequence(sequence);
            setSelectedSlideId(slideDetails.collateralTypeId);
            await UpdateCurrentSlide({ slide: slideDetails }, eventId);
        } catch (error) {
            console.error("Error changing slide:", error);
        } finally {
          setIsSyncing(false); // Re-enable controls after sync
        }

    },
    [slides, eventId, role, slideControlEnabled, currentSlideSequence, isSyncing]
);
  const handleNextSlide = useCallback(() => {
    const currentIndex = slides.findIndex((slide) => slide.sequence === currentSlideSequence);
    
    if (currentIndex !== -1) {
      let nextSlide: Slide | null = null;
      if (slides[currentIndex].type === "pollQuestions") {
        nextSlide = {
          ...slides[currentIndex],
          type: "pollresult",
          sequence: slides[currentIndex].sequence + 0.1,
        };
      } else if (slides[currentIndex].type === "pollresult") {
        nextSlide =
          slides.find((slide) => Math.floor(slide.sequence) > Math.floor(currentSlideSequence)) ||
          null;
      } else if (currentIndex < slides.length - 1) {
        nextSlide = slides[currentIndex + 1];
      }
  
      if (nextSlide) {
        handleSlideChange(nextSlide.sequence);
      } else {
        console.warn("No next slide found");
      }
    }
  }, [currentSlideSequence, slides, handleSlideChange]);
  
  const handlePreviousSlide = useCallback(() => {
    const currentIndex = slides.findIndex((slide) => slide.sequence === currentSlideSequence);
  
    if (currentIndex <= 0) {
      console.warn("Already at the first slide");
      return;
    }
  
    let previousSlide: Slide = slides[currentIndex - 1] || null;
  
    if (slides[currentIndex].type === "pollresult") {
      previousSlide =
        slides.find(
          (slide) =>
            slide.type === "pollQuestions" &&
            slide.sequence === Math.floor(currentSlideSequence)
        ) || previousSlide;
    } else if (previousSlide?.type === "pollQuestions") {
      const pollResultSlide =
        slides.find(
          (slide) =>
            slide.type === "pollresult" && slide.sequence === previousSlide.sequence + 0.1
        ) || null;
  
      if (pollResultSlide) {
        previousSlide = pollResultSlide;
      }
    }
  
    if (previousSlide) {
      handleSlideChange(previousSlide.sequence);
    } else {
      console.warn("No valid previous slide found");
    }
  }, [currentSlideSequence, slides, handleSlideChange]);
  
  return (
    <SlideContext.Provider
      value={{
        slides,
        currentSlideSequence,
        handleNextSlide,
        handlePreviousSlide,
        handleSlideChange,
        setSlides,
        setCurrentSlideSequence,
        slideControlEnabled,
        setSlideControlEnabled,
        isSyncing
      }}
    >
      {children}
    </SlideContext.Provider>
  );
};
export const useSlide = () => {
  const context = useContext(SlideContext);
  if (!context) {
    throw new Error("useSlide must be used within a SlideProvider");
  }
  return context;
};